import { SVGIcon } from '@superops/super-icons';
import {
  Badge,
  Button,
  DisplayText,
  Dropdown,
  TextStyle
} from '@superops/ui-design-system';
import { isEmpty, isNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import { getTranslatedText, useActivityTracker } from '@nile/components';
import {
  DropdownIndicator,
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/banyan/src/Pebbles/select';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { onQuerySearch } from '@nile/reports/src/modules/EfficiencyReport/graphql/resolver';

function SectionHeader({
  name,
  description,
  hideIcon = false,
  size = 'h2',
  action = null,
  section
}) {
  const intl = useIntl();

  const getHeaderAction = () => {
    switch (action?.type) {
      case 'DROPDOWN':
        return <FilterComp {...action} intl={intl} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="flex actr g15" id={section}>
        {!hideIcon && (
          <div className="flex-center">
            <Badge status="critical" radial>
              <SVGIcon name="ClientLogo" size="sm" />
            </Badge>
          </div>
        )}
        <div>
          <DisplayText element={size} weight="SemiBold">
            <FormattedMessage id={name || ' '} />
          </DisplayText>
          {!isEmpty(description) && (
            <div className="mt8">
              <TextStyle>
                <FormattedMessage id={description || ' '} />
              </TextStyle>
            </div>
          )}
        </div>
        {action && getHeaderAction()}
      </div>
    </>
  );
}

const FilterComp = ({
  value,
  setValue,
  options = [],
  intl,
  placeholder = 'app.chooseValue.pl',
  inline = false,
  api = null,
  isClient = false,
  entity
}) => {
  const [data, setData] = useState([]);

  // Only get query and client if api is provided
  const { client, query } = api
    ? onQuerySearch(api) || { client: null, query: null }
    : { client: null, query: null };

  const getValue = (prop) => {
    if (!prop) return '';
    if (prop.hasOwnProperty('identifier')) return prop?.identifier;
    return prop?.name;
  };

  // Define loading state separately since we may not use useLazyQuery
  const [loading, setLoading] = useState(false);

  // Only define and use getFilters if we have a valid query
  const getFiltersResult = query
    ? useLazyQuery(query, {
        client,
        fetchPolicy: 'cache-and-network',
        variables: {
          input: {
            entity: entity
          }
        },
        onCompleted(res) {
          setLoading(false);
          const filterOptions =
            res && res[api]
              ? res[api].map((el) => ({
                  name: getTranslatedText(intl, el?.name || ''),
                  label: getTranslatedText(intl, el?.name || ''),
                  value: getValue(el)
                })) || []
              : [];

          setData(filterOptions);
        }
      })
    : [() => {}, { loading: false }];

  // Destructure the result
  const [getFilters] = getFiltersResult;

  // Safely map options or use fetched data
  const dropOptions = !api
    ? Array.isArray(options)
      ? options.map((el) => ({
          ...el,
          name: getTranslatedText(intl, el?.name || ''),
          label: getTranslatedText(intl, el?.label || '')
        }))
      : []
    : data;

  const findValue = (option) => {
    if (!option) return false;
    return option.value === value;
  };

  useEffect(() => {
    if (!isEmpty(query) && typeof getFilters === 'function') {
      setLoading(true);
      getFilters();
    }
  }, [query, getFilters]);

  return (
    <div className={`flex f11 ${inline ? '' : 'jend'}`}>
      <div className="w250 bshadow rounded-2xl noborder flex-center g10 px10">
        <SVGIcon name={isClient ? 'ClientLogo' : 'Filter'} />
        <Dropdown
          value={
            Array.isArray(dropOptions)
              ? dropOptions.find((el) => findValue(el))
              : null
          }
          onChange={(val) => {
            if (isClient) {
              useActivityTracker({
                event: 'EFFICIENCY_DELVE_DEEPER_CLIENT',
                data: {
                  clientName: val?.label
                }
              });
            } else {
              useActivityTracker({
                event: 'EFFICIENCY_REPORT_FILTER',
                data: {
                  api: api,
                  option: val?.label
                }
              });
            }
            if (setValue && val) setValue(val.value);
          }}
          options={dropOptions}
          isLoading={loading}
          isDisabled={loading}
          components={{ IndicatorSeparator, DropdownIndicator }}
          classNamePrefix="value_selector"
          className="full-width noborder"
          formatGroupLabel={formatGroupLabel}
          menuPlacement="auto"
          placeholder={getTranslatedText(
            intl,
            placeholder || 'app.chooseValue.pl'
          )}
          closeMenuOnScroll={(e) => {
            return (
              e.target &&
              e.target.classList &&
              !(e.target.classList.value.indexOf('menu-list') > -1)
            );
          }}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 })
          }}
        />
      </div>
    </div>
  );
};

export default SectionHeader;
